<template>
  <v-card flat class="text-center">
    <div class="zipCodeForm">
      <template v-if="!invalidZipCode">
        <h1 class="text-center fw-normal mb-4">
          Please enter in <b class="text-green">postal code</b> to see if we operate in your area
          <!-- <span class="text-green">find instructors</span> near you. -->
        </h1>
        <v-card-text>
          <v-form ref="zipCodeForm" @submit.prevent>
            <div class="form-group">
              <v-text-field
                v-model="zipCode"
                outlined
                dense
                label="Postal Code *"
                :error-messages="errorMessage"
                @input="formatPostalCode"
                hint="Enter a valid Ontario postal code (e.g., K1A 0A6)"
                persistent-hint
                :rules="requiredRules"
                autocomplete="off"
                data-form-type="postal-code"
                data-lpignore="true"
                required
              />
            </div>

            <div class="form-group">
              <v-btn type="button" @click="validateZipCode" color="primary" :disabled="!zipCode">Continue</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </template>

      <template v-else>
        <h1 class="text-center fw-normal mb-4">
          Sorry, we couldn't find your postal code.<br>
          Please enter your full address.
        </h1>
        <v-card-text>
          <v-form ref="addressForm" @submit.prevent>
            <div class="form-group">
              <google-autocomplete
                id="fullAddress"
                ref="addressInput"
                :api-key="'AIzaSyB24tCL14XhXd19PcnlF7Or2m4jhjm_Fa0'"
                outlined
                dense
                :country="['ca']"
                :types="['address']"
                label="Enter your full address (Only Ontario)*"
                @place_changed="getAddressData"
                :error-messages="addressErrorMessage ? [addressErrorMessage] : []"
                :rules="requiredRules"
                autocomplete="off"
                data-form-type="address"
                data-lpignore="true"
                required
              />
            </div>
            <p class="text-center errorMessage">{{ addressErrorMessage }}</p>
            <div class="form-group">
              <v-btn type="button" @click="continueWithAddress" color="primary" :disabled="!address">Continue</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </template>
    </div>
  </v-card>
</template>

<script>
import validationRules from '@/validation-rules'

export default {
  name: 'ZipCodeForm',
  data () {
    return {
      zipCode: '',
      address: '',
      errorMessage: '',
      addressErrorMessage: '',
      invalidZipCode: false,
      addressComponents: {},
      latitude: null,
      longitude: null,
      requiredRules: [v => !!v || 'This field is required'],
      addressSelected: false,
      lastSelectedAddress: null,
      ...validationRules
    }
  },
  methods: {
    isOntarioPostalCode (code) {
      // Ontario postal codes start with K, L, M, N, or P
      const ontarioPattern = /^[KLMNP][0-9][A-Z] ?[0-9][A-Z][0-9]$/i
      return ontarioPattern.test(code)
    },

    formatPostalCodeForValidation (code) {
      if (!code) return ''

      // Remove all non-alphanumeric characters and convert to uppercase
      const formattedCode = code.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()

      // If it's a full Ontario postal code (6 characters), extract the first 3
      if (formattedCode.length === 6 && /^[KLMNP][0-9][A-Z][0-9][A-Z][0-9]$/i.test(formattedCode)) {
        return formattedCode.substring(0, 3)
      }

      // If it's already 3 characters, return as is
      if (formattedCode.length === 3 && /^[KLMNP][0-9][A-Z]$/i.test(formattedCode)) {
        return formattedCode
      }

      // Return the original code if it doesn't match expected patterns
      return code
    },
    validateZipCode () {
      this.errorMessage = ''

      if (!this.zipCode) {
        this.errorMessage = 'Postal code is required'
        return
      }

      if (this.isOntarioPostalCode(this.zipCode)) {
        // Valid Ontario postal code - emit event to parent instead of navigating
        this.$emit('continue')
      } else {
        // Invalid or non-Ontario postal code
        this.invalidZipCode = true
      }
    },
    getAddressData (addressData) {
      console.log('Address data received:', addressData)
      this.lastSelectedAddress = addressData
      this.address = addressData.formatted_address || addressData.name || ''

      try {
        // Extract address components from Google Places result
        this.addressComponents = {
          addressOne: addressData.name ||
            (addressData.street_number && addressData.route
              ? `${addressData.street_number} ${addressData.route}`
              : addressData.formatted_address?.split(',')[0] || ''),
          postalCode: this.formatPostalCodeForValidation(addressData.postal_code || addressData.postal_code_basic || ''),
          city: addressData.locality || addressData.city || '',
          province: addressData.administrative_area_level_1 || addressData.province || addressData.state || '',
          country: addressData.country || addressData.country_code || ''
        }

        console.log('Postal code processing:', {
          original: addressData.postal_code,
          basic: addressData.postal_code_basic,
          formatted: this.addressComponents.postalCode
        })

        // Verify the address is in Ontario
        const province = this.addressComponents.province.toUpperCase()
        if (province !== 'ONTARIO' && province !== 'ON') {
          this.addressErrorMessage = 'Sorry, we currently don\'t operate here'
          this.addressSelected = false
          return
        }

        // Make sure latitude and longitude are properly set
        this.latitude = addressData.latitude || null
        this.longitude = addressData.longitude || null

        console.log('Processed components:', this.addressComponents)
        console.log('Coordinates:', this.latitude, this.longitude)

        // Reset any previous error messages
        this.addressErrorMessage = ''

        // Mark that an address was selected from dropdown
        this.addressSelected = true
      } catch (error) {
        console.error('Error processing address data:', error)
        this.addressErrorMessage = 'There was an error processing the selected address. Please try again.'
      }
    },
    onAddressInput () {
      // Method no longer needed - removed v-model and @input event
    },
    continueWithAddress () {
      if (!this.address.trim()) {
        this.addressErrorMessage = 'Please enter your full address'
        return
      }

      console.log('Continuing with address:', this.address)
      console.log('Address selected flag:', this.addressSelected)
      console.log('Last selected address data:', this.lastSelectedAddress)

      // More flexible validation - if the address appears to be a genuine Google Places selection
      // but our flag didn't catch it, we can still proceed
      if (!this.addressSelected && this.lastSelectedAddress) {
        console.log('Address selected flag was false but we have address data - proceeding anyway')
        this.addressSelected = true
      }

      // Check if address was selected from dropdown
      if (!this.addressSelected) {
        this.addressErrorMessage = 'Please select an address from the dropdown list'
        return
      }

      // Verify the address is in Ontario before continuing
      const province = this.addressComponents.province?.toUpperCase() || ''
      if (province !== 'ONTARIO' && province !== 'ON') {
        this.addressErrorMessage = 'Sorry, we currently don\'t operate here'
        return
      }

      // For basic addresses that may not have all components, create minimal required data
      if (!this.addressComponents.addressOne) {
        this.addressComponents.addressOne = this.address
      }

      // If we're missing coordinates, don't block the user but log a warning
      if (!this.latitude || !this.longitude) {
        console.warn('Address selected without coordinates')
      }

      // Store address data in localStorage or state management for use in next screens
      const addressData = {
        fullAddress: this.address,
        components: this.addressComponents,
        coordinates: {
          latitude: this.latitude,
          longitude: this.longitude
        }
      }

      // Store data but don't navigate - emit event instead
      localStorage.setItem('userAddress', JSON.stringify(addressData))

      // Emit continue event to the parent component instead of navigating
      this.$emit('continue', addressData)
    },
    formatPostalCode () {
      if (!this.zipCode) return

      // Remove all non-alphanumeric characters
      let formattedCode = this.zipCode.replace(/[^a-zA-Z0-9]/g, '')

      // Convert to uppercase
      formattedCode = formattedCode.toUpperCase()

      // Insert space after the 3rd character (if length > 3)
      if (formattedCode.length > 3) {
        formattedCode = formattedCode.slice(0, 3) + ' ' + formattedCode.slice(3)
      }

      // Limit to maximum 7 characters (including space)
      formattedCode = formattedCode.slice(0, 7)

      // Update the model
      this.zipCode = formattedCode
    }
  }
}
</script>
<style>
.zipCodeForm {
  max-width: 400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.text-green {
  color: #2d9a47;
}
.fw-normal {
  font-weight: normal;
}

.zipCodeForm .v-btn {
  border-radius: 30px;
  width: 200px;
}
.errorMessage {
  color: red;
}
</style>
